const P = (x, mt) => ({ t: "p", x, mt });
const H = (x) => ({ t: "h3", x });
const UL = (items) => ({ t: "ul", items });
const OL = (items) => ({ t: "ol", items });
const BOX = (items) => ({ t: "box", items });
const CALLOUT = (c, title, x, items, after) => ({ t: "callout", c, title, x, items, after });

const termsOfService = {
  en: {
    termsPage: {
      back: "Back to Home",
      title: "Terms of Service",
      lastUpdated: "Last Updated: January 18, 2025",
      links: { privacy: "Privacy Policy", cookies: "Cookie Policy", refund: "Refund Policy" },
      footer: "By using EReMS Cameroon, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.",
      sections: [
        { h: "1. Agreement to Terms", blocks: [
          P("By accessing or using EReMS Cameroon (Election Results Management System), you agree to be bound by these Terms of Service and all applicable laws and regulations. If you do not agree with any part of these terms, you may not use our service."),
        ] },
        { h: "2. Description of Service", blocks: [
          P("EReMS Cameroon is a comprehensive election results management platform that provides:"),
          UL([
            "Real-time election result tracking and reporting",
            "Result verification and approval workflows",
            "Interactive maps and data visualization",
            "Analytics and comprehensive reporting",
            "Multi-user role-based access control",
            "API access for integration",
          ]),
        ] },
        { h: "3. User Accounts and Registration", blocks: [
          H("3.1 Account Creation"),
          UL([
            "You must be at least 18 years old to create an account",
            "You must provide accurate and complete information",
            "You are responsible for maintaining account security",
            "You must not share your account credentials",
            "One person may only have one account",
          ]),
          H("3.2 Account Responsibilities"),
          UL([
            "You are responsible for all activities under your account",
            "Notify us immediately of unauthorized access",
            "We are not liable for losses from unauthorized use",
            "You must comply with all applicable laws",
          ]),
        ] },
        { h: "4. User Roles and Permissions", icon: "scale", blocks: [
          P("EReMS Cameroon provides different access levels:"),
          UL([
            "**Super Admin:** Full system access, user management",
            "**Election Manager:** Manage elections, approve results",
            "**Media:** View and report on results",
            "**Observer:** Monitor elections and results",
            "**Reporter:** Submit results from polling stations",
            "**Viewer:** Read-only access to public results",
          ]),
          P("Each role has specific permissions. You may only perform actions authorized for your role.", true),
        ] },
        { h: "5. Acceptable Use Policy", blocks: [
          H("5.1 Prohibited Activities"),
          P("You agree NOT to:"),
          UL([
            "Submit false or fraudulent election results",
            "Manipulate or tamper with election data",
            "Access accounts or data without authorization",
            "Use the service for illegal activities",
            "Interfere with service operations",
            "Reverse engineer or copy the platform",
            "Upload malicious code or viruses",
            "Scrape or harvest data without permission",
            "Impersonate others or misrepresent affiliation",
            "Spam, harass, or abuse other users",
          ]),
          H("5.2 Data Integrity"),
          P("Users must submit accurate election data. Deliberate submission of false information may result in account termination and legal action."),
        ] },
        { h: "6. Subscription and Payments", blocks: [
          H("6.1 Subscription Plans"),
          UL([
            "**Free:** Limited features, basic access",
            "**Basic ($200/month):** Full map access, 500 API calls/day",
            "**Professional ($500/month):** All features, 5000 API calls/day",
            "**Enterprise:** Custom pricing and features",
          ]),
          H("6.2 Payment Terms"),
          UL([
            "Subscriptions are billed monthly in advance",
            "Payments processed securely via Stripe",
            "All fees are in US Dollars (USD)",
            "Prices subject to change with 30 days notice",
            "Failed payments may result in service suspension",
          ]),
          H("6.3 Auto-Renewal"),
          P("Subscriptions automatically renew unless cancelled before the renewal date. You can cancel anytime through your account settings."),
        ] },
        { h: "7. Cancellation and Refunds", blocks: [
          P("See our [Refund Policy](/refund-policy) for detailed information about cancellations, refunds, and subscription terms."),
        ] },
        { h: "8. Intellectual Property Rights", blocks: [
          H("8.1 Our Rights"),
          P("EReMS Cameroon and all its content, features, and functionality are owned by us and protected by copyright, trademark, and other intellectual property laws."),
          H("8.2 Your Rights"),
          P("You retain ownership of election data you submit. By submitting data, you grant us a license to use, store, and display it as necessary to provide the service."),
          H("8.3 Public Election Data"),
          P("Approved election results become part of the public record and may be accessed by anyone."),
        ] },
        { h: "9. Disclaimer of Warranties", icon: "alert", blocks: [
          CALLOUT("yellow", "IMPORTANT - READ CAREFULLY:",
            "THE SERVICE IS PROVIDED \"AS IS\" AND \"AS AVAILABLE\" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:",
            [
              "Warranties of merchantability",
              "Fitness for a particular purpose",
              "Non-infringement",
              "Uninterrupted or error-free operation",
              "Accuracy or reliability of data",
            ]),
        ] },
        { h: "10. Limitation of Liability", blocks: [
          CALLOUT("red", "LIABILITY DISCLAIMER:",
            "TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO:",
            [
              "Loss of profits or revenue",
              "Loss of data",
              "Loss of business opportunities",
              "Service interruptions",
              "Third-party actions",
            ],
            "Our total liability shall not exceed the amount you paid us in the 12 months preceding the claim."),
        ] },
        { h: "11. Indemnification", blocks: [
          P("You agree to indemnify and hold harmless EReMS Cameroon and its officers, directors, employees, and agents from any claims, damages, losses, liabilities, and expenses arising from:"),
          UL([
            "Your use of the service",
            "Your violation of these terms",
            "Your violation of any rights of another",
            "Your submission of false or fraudulent data",
          ]),
        ] },
        { h: "12. Service Availability", blocks: [
          P("We strive to provide 99.9% uptime but do not guarantee uninterrupted service. We may:"),
          UL([
            "Perform scheduled maintenance with advance notice",
            "Conduct emergency maintenance as needed",
            "Temporarily suspend service for security reasons",
            "Modify or discontinue features with notice",
          ]),
        ] },
        { h: "13. Termination", blocks: [
          H("13.1 By You"),
          P("You may terminate your account at any time through account settings."),
          H("13.2 By Us"),
          P("We may suspend or terminate your account if you:"),
          UL([
            "Violate these Terms of Service",
            "Submit false or fraudulent data",
            "Fail to pay subscription fees",
            "Engage in illegal activities",
            "Pose a security risk",
          ]),
          H("13.3 Effect of Termination"),
          P("Upon termination, your access will cease immediately. We may retain certain data as required by law or for legitimate business purposes."),
        ] },
        { h: "14. Governing Law", blocks: [
          P("These Terms shall be governed by and construed in accordance with the laws of Cameroon, without regard to conflict of law principles."),
        ] },
        { h: "15. Dispute Resolution", blocks: [
          P("Any disputes arising from these Terms or use of the service shall be resolved through:"),
          OL([
            "Good faith negotiation between parties",
            "Mediation if negotiation fails",
            "Arbitration in Yaoundé, Cameroon if mediation fails",
          ]),
        ] },
        { h: "16. Changes to Terms", blocks: [
          P("We reserve the right to modify these Terms at any time. We will notify users of material changes via:"),
          UL(["Email notification", "In-app notification", "Website banner"]),
          P("Continued use after changes constitutes acceptance of new terms.", true),
        ] },
        { h: "17. Severability", blocks: [
          P("If any provision of these Terms is found to be unenforceable, the remaining provisions will remain in full force and effect."),
        ] },
        { h: "18. Contact Information", blocks: [
          P("For questions about these Terms, contact us:"),
          BOX([
            "**Operator:** M-TECH",
            "**Email:** mboatechnologyllc@gmail.com",
          ]),
        ] },
        { h: "19. Entire Agreement", blocks: [
          P("These Terms, together with our Privacy Policy and other referenced policies, constitute the entire agreement between you and EReMS Cameroon regarding use of the service."),
        ] },
      ],
    },
  },
  fr: {
    termsPage: {
      back: "Retour à l'accueil",
      title: "Conditions d'utilisation",
      lastUpdated: "Dernière mise à jour : 18 janvier 2025",
      links: { privacy: "Politique de confidentialité", cookies: "Politique relative aux cookies", refund: "Politique de remboursement" },
      footer: "En utilisant EReMS Cameroon, vous reconnaissez avoir lu et compris les présentes Conditions d'utilisation et acceptez d'y être lié.",
      sections: [
        { h: "1. Acceptation des conditions", blocks: [
          P("En accédant à EReMS Cameroon (Election Results Management System) ou en l'utilisant, vous acceptez d'être lié par les présentes Conditions d'utilisation ainsi que par l'ensemble des lois et réglementations applicables. Si vous n'acceptez pas l'une quelconque de ces conditions, vous ne pouvez pas utiliser notre service."),
        ] },
        { h: "2. Description du service", blocks: [
          P("EReMS Cameroon est une plateforme complète de gestion des résultats électoraux qui propose :"),
          UL([
            "Le suivi et la communication des résultats électoraux en temps réel",
            "Des processus de vérification et d'approbation des résultats",
            "Des cartes interactives et de la visualisation de données",
            "Des analyses et des rapports détaillés",
            "Un contrôle d'accès multi-utilisateurs basé sur les rôles",
            "Un accès API à des fins d'intégration",
          ]),
        ] },
        { h: "3. Comptes d'utilisateur et inscription", blocks: [
          H("3.1 Création de compte"),
          UL([
            "Vous devez avoir au moins 18 ans pour créer un compte",
            "Vous devez fournir des informations exactes et complètes",
            "Vous êtes responsable de la sécurité de votre compte",
            "Vous ne devez pas partager vos identifiants de connexion",
            "Une personne ne peut détenir qu'un seul compte",
          ]),
          H("3.2 Responsabilités liées au compte"),
          UL([
            "Vous êtes responsable de toutes les activités effectuées depuis votre compte",
            "Informez-nous immédiatement de tout accès non autorisé",
            "Nous ne sommes pas responsables des pertes résultant d'une utilisation non autorisée",
            "Vous devez respecter toutes les lois applicables",
          ]),
        ] },
        { h: "4. Rôles et autorisations des utilisateurs", icon: "scale", blocks: [
          P("EReMS Cameroon propose différents niveaux d'accès :"),
          UL([
            "**Super administrateur :** accès complet au système, gestion des utilisateurs",
            "**Gestionnaire d'élections :** gestion des élections, approbation des résultats",
            "**Médias :** consultation des résultats et reportage à leur sujet",
            "**Observateur :** suivi des élections et des résultats",
            "**Rapporteur :** soumission des résultats depuis les bureaux de vote",
            "**Lecteur :** accès en lecture seule aux résultats publics",
          ]),
          P("Chaque rôle dispose d'autorisations spécifiques. Vous ne pouvez effectuer que les actions autorisées pour votre rôle.", true),
        ] },
        { h: "5. Politique d'utilisation acceptable", blocks: [
          H("5.1 Activités interdites"),
          P("Vous vous engagez à NE PAS :"),
          UL([
            "Soumettre des résultats électoraux faux ou frauduleux",
            "Manipuler ou altérer les données électorales",
            "Accéder à des comptes ou à des données sans autorisation",
            "Utiliser le service à des fins illégales",
            "Perturber le fonctionnement du service",
            "Procéder à de la rétro-ingénierie ou copier la plateforme",
            "Téléverser du code malveillant ou des virus",
            "Extraire ou collecter des données sans autorisation",
            "Usurper l'identité d'autrui ou déformer votre affiliation",
            "Envoyer du spam, harceler ou maltraiter d'autres utilisateurs",
          ]),
          H("5.2 Intégrité des données"),
          P("Les utilisateurs doivent soumettre des données électorales exactes. La soumission délibérée d'informations fausses peut entraîner la résiliation du compte et des poursuites judiciaires."),
        ] },
        { h: "6. Abonnement et paiements", blocks: [
          H("6.1 Formules d'abonnement"),
          UL([
            "**Gratuit :** fonctionnalités limitées, accès de base",
            "**Basique (200 $/mois) :** accès complet à la carte, 500 appels API/jour",
            "**Professionnel (500 $/mois) :** toutes les fonctionnalités, 5000 appels API/jour",
            "**Entreprise :** tarification et fonctionnalités sur mesure",
          ]),
          H("6.2 Conditions de paiement"),
          UL([
            "Les abonnements sont facturés mensuellement à l'avance",
            "Les paiements sont traités de manière sécurisée via Stripe",
            "Tous les frais sont exprimés en dollars américains (USD)",
            "Les prix sont susceptibles de changer moyennant un préavis de 30 jours",
            "Un paiement refusé peut entraîner la suspension du service",
          ]),
          H("6.3 Renouvellement automatique"),
          P("Les abonnements sont renouvelés automatiquement, sauf résiliation avant la date de renouvellement. Vous pouvez résilier à tout moment depuis les paramètres de votre compte."),
        ] },
        { h: "7. Résiliation et remboursements", blocks: [
          P("Consultez notre [Politique de remboursement](/refund-policy) pour plus d'informations sur les résiliations, les remboursements et les conditions d'abonnement."),
        ] },
        { h: "8. Droits de propriété intellectuelle", blocks: [
          H("8.1 Nos droits"),
          P("EReMS Cameroon, ainsi que l'ensemble de son contenu, de ses fonctionnalités et de ses caractéristiques, nous appartiennent et sont protégés par le droit d'auteur, le droit des marques et d'autres lois relatives à la propriété intellectuelle."),
          H("8.2 Vos droits"),
          P("Vous demeurez propriétaire des données électorales que vous soumettez. En soumettant des données, vous nous accordez une licence pour les utiliser, les stocker et les afficher dans la mesure nécessaire à la fourniture du service."),
          H("8.3 Données électorales publiques"),
          P("Les résultats électoraux approuvés font partie du domaine public et peuvent être consultés par tous."),
        ] },
        { h: "9. Exclusion de garanties", icon: "alert", blocks: [
          CALLOUT("yellow", "IMPORTANT – À LIRE ATTENTIVEMENT :",
            "LE SERVICE EST FOURNI « EN L'ÉTAT » ET « SELON LA DISPONIBILITÉ », SANS GARANTIE D'AUCUNE SORTE, EXPRESSE OU IMPLICITE, Y COMPRIS, SANS S'Y LIMITER :",
            [
              "Les garanties de qualité marchande",
              "L'adéquation à un usage particulier",
              "L'absence de contrefaçon",
              "Un fonctionnement ininterrompu ou sans erreur",
              "L'exactitude ou la fiabilité des données",
            ]),
        ] },
        { h: "10. Limitation de responsabilité", blocks: [
          CALLOUT("red", "EXCLUSION DE RESPONSABILITÉ :",
            "DANS TOUTE LA MESURE PERMISE PAR LA LOI, NOUS NE SAURIONS ÊTRE TENUS RESPONSABLES DE TOUT DOMMAGE INDIRECT, ACCESSOIRE, SPÉCIAL, CONSÉCUTIF OU PUNITIF, Y COMPRIS, SANS S'Y LIMITER :",
            [
              "La perte de bénéfices ou de revenus",
              "La perte de données",
              "La perte d'opportunités commerciales",
              "Les interruptions de service",
              "Les actions de tiers",
            ],
            "Notre responsabilité totale ne saurait excéder le montant que vous nous avez versé au cours des 12 mois précédant la réclamation."),
        ] },
        { h: "11. Indemnisation", blocks: [
          P("Vous vous engagez à indemniser et à dégager de toute responsabilité EReMS Cameroon ainsi que ses dirigeants, administrateurs, employés et agents à l'égard de toute réclamation, tout dommage, toute perte, toute responsabilité et tous frais découlant :"),
          UL([
            "De votre utilisation du service",
            "De votre violation des présentes conditions",
            "De votre violation des droits d'un tiers",
            "De votre soumission de données fausses ou frauduleuses",
          ]),
        ] },
        { h: "12. Disponibilité du service", blocks: [
          P("Nous nous efforçons d'assurer une disponibilité de 99,9 % mais ne garantissons pas un service ininterrompu. Nous pouvons :"),
          UL([
            "Effectuer une maintenance programmée avec préavis",
            "Effectuer une maintenance d'urgence en cas de besoin",
            "Suspendre temporairement le service pour des raisons de sécurité",
            "Modifier ou supprimer des fonctionnalités moyennant préavis",
          ]),
        ] },
        { h: "13. Résiliation", blocks: [
          H("13.1 Par vous"),
          P("Vous pouvez résilier votre compte à tout moment depuis les paramètres du compte."),
          H("13.2 Par nous"),
          P("Nous pouvons suspendre ou résilier votre compte si vous :"),
          UL([
            "Enfreignez les présentes Conditions d'utilisation",
            "Soumettez des données fausses ou frauduleuses",
            "Ne payez pas les frais d'abonnement",
            "Vous livrez à des activités illégales",
            "Présentez un risque pour la sécurité",
          ]),
          H("13.3 Effets de la résiliation"),
          P("En cas de résiliation, votre accès cessera immédiatement. Nous pouvons conserver certaines données lorsque la loi l'exige ou pour des raisons commerciales légitimes."),
        ] },
        { h: "14. Droit applicable", blocks: [
          P("Les présentes Conditions sont régies et interprétées conformément aux lois du Cameroun, sans égard aux principes de conflit de lois."),
        ] },
        { h: "15. Règlement des litiges", blocks: [
          P("Tout litige découlant des présentes Conditions ou de l'utilisation du service sera résolu par :"),
          OL([
            "Une négociation de bonne foi entre les parties",
            "Une médiation en cas d'échec de la négociation",
            "Un arbitrage à Yaoundé (Cameroun) en cas d'échec de la médiation",
          ]),
        ] },
        { h: "16. Modifications des conditions", blocks: [
          P("Nous nous réservons le droit de modifier les présentes Conditions à tout moment. Nous informerons les utilisateurs des modifications importantes par :"),
          UL(["Notification par e-mail", "Notification dans l'application", "Bannière sur le site web"]),
          P("La poursuite de l'utilisation après modification vaut acceptation des nouvelles conditions.", true),
        ] },
        { h: "17. Divisibilité", blocks: [
          P("Si une disposition des présentes Conditions est jugée inapplicable, les autres dispositions demeureront pleinement en vigueur."),
        ] },
        { h: "18. Coordonnées", blocks: [
          P("Pour toute question concernant les présentes Conditions, contactez-nous :"),
          BOX([
            "**Exploitant :** M-TECH",
            "**E-mail :** mboatechnologyllc@gmail.com",
          ]),
        ] },
        { h: "19. Intégralité de l'accord", blocks: [
          P("Les présentes Conditions, ainsi que notre Politique de confidentialité et les autres politiques mentionnées, constituent l'intégralité de l'accord entre vous et EReMS Cameroon concernant l'utilisation du service."),
        ] },
      ],
    },
  },
};

export default termsOfService;
